import { useState, useEffect } from "react";
import { getProjects, createProject, type Project } from "@/lib/projectStore";
import ProjectCard from "@/components/ProjectCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, FolderOpen } from "lucide-react";

export default function Dashboard() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [methodology, setMethodology] = useState<"agile" | "waterfall">("agile");
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const data = await getProjects();
    setProjects(data);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const handleCreate = async () => {
    if (!name.trim()) return;
    await createProject(name.trim(), desc.trim());
    setName("");
    setDesc("");
    setMethodology("agile");
    setOpen(false);
    refresh();
  };

  const activeProjects = projects.filter((p) => p.status === "active");
  const draftProjects = projects.filter((p) => p.status === "draft");
  const archivedProjects = projects.filter((p) => p.status === "archived");

  return (
    <div className="p-6 lg:p-8 max-w-6xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">
            Start Here
          </h1>
          <p className="text-sm text-muted-foreground font-body mt-1">
            Create your first project and explore the full BA workflow
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1.5">
              <Plus className="h-4 w-4" /> New Project
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">New Project</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <Input
                placeholder="Project name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              />
              <Textarea
                placeholder="Brief description (optional)"
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                rows={2}
              />
              <div>
                <label className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-1.5 block">
                  Methodology
                </label>
                <Select value={methodology} onValueChange={(v) => setMethodology(v as any)}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agile">Agile (Sprints)</SelectItem>
                    <SelectItem value="waterfall">Waterfall</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleCreate} className="w-full">
                Create Project
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-20 text-sm text-muted-foreground">Loading projects…</div>
      ) : projects.length === 0 ? (
        <div className="text-center py-20 animate-fade-in">
          <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
          <h2 className="font-display text-lg font-semibold text-foreground mb-1">
            No projects yet
          </h2>
          <p className="text-sm text-muted-foreground mb-6 font-body">
            Create your first BA project to start documenting requirements and flows
          </p>
          <Button onClick={() => setOpen(true)} size="sm" className="gap-1.5">
            <Plus className="h-4 w-4" /> Create Project
          </Button>
        </div>
      ) : (
        <div className="space-y-8">
          {activeProjects.length > 0 && (
            <ProjectSection title="Active" projects={activeProjects} onDeleted={refresh} />
          )}
          {draftProjects.length > 0 && (
            <ProjectSection title="Drafts" projects={draftProjects} onDeleted={refresh} />
          )}
          {archivedProjects.length > 0 && (
            <ProjectSection title="Archived" projects={archivedProjects} onDeleted={refresh} />
          )}
        </div>
      )}
    </div>
  );
}

function ProjectSection({ title, projects, onDeleted }: { title: string; projects: Project[]; onDeleted: () => void }) {
  return (
    <div>
      <h2 className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-3">
        {title} ({projects.length})
      </h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {projects.map((p) => (
          <ProjectCard key={p.id} project={p} onDeleted={onDeleted} />
        ))}
      </div>
    </div>
  );
}
